/**
 * What plays (spec 006): a scene has one animation model, its authored
 * keyframes. Its dialog lines' expressions (spec 026) are derived `state`
 * keys, computed and never stored, merged with the authored `state` keys by
 * the precedence rule below. Nothing here branches on a mode: a stray
 * field Storyboard left is ignored.
 *
 * The precedence rule, per sprite and orientation `o`:
 * 1. The authored `state` keys that count in `o` are the interpolator's
 *    candidates: the `o`-tagged ones when the sprite has any in portrait or
 *    landscape, else the untagged and desktop-tagged ones.
 * 2. A line's derived keys (start and end together) are dropped when a
 *    counting authored `state` key lies within the line's span, ends
 *    included: the hand wins.
 * 3. A kept end key (`backToDefault`) takes the state the counting authored
 *    keys show at that time, else the sprite's `defaultState`.
 * 4. Kept derived keys are tagged `o` when the counting authored keys are
 *    `o`-tagged, else untagged.
 * 5. The merged stream is sorted by time; at equal times a derived key comes
 *    before an authored one and an end key before a start key.
 *
 * Spec 012's `toPlayableScene` applies the same rule (through
 * `resolveLineExpressionKeys`) in the player and at publish, so for any
 * scene and orientation the editor and the player agree.
 */

import type { Keyframe, KeyframeTrack, Orientation, SceneData, Sprite } from '../@types';
import { computeDialogTimeline } from './sceneTiming';
import { activeKey, candidateKeyframes } from './interpolate';

/** The parts of a scene that decide what plays. */
export type PlaybackScene = Pick<SceneData, 'sprites' | 'dialogs'> & { keyframes?: KeyframeTrack[] | null };

/** One line's expression on a sprite, with the line's span (`computeDialogTimeline`). */
export interface LineExpressionSpan {
  lineId: string;
  spriteId: string;
  state: string;
  backToDefault: boolean;
  startMs: number;
  endMs: number;
  /** Authoring order (dialogs, then lines), the tie-break at equal times. */
  order: number;
}

/**
 * Every line expression that can play: lines whose `expression` names a
 * sprite of the scene and one of its states, with their spans. A line whose
 * sprite or state is gone has none.
 */
export function lineExpressionSpans(scene: Pick<PlaybackScene, 'sprites' | 'dialogs'>): LineExpressionSpan[] {
  const out: LineExpressionSpan[] = [];
  for (const dialog of scene.dialogs ?? []) {
    const timeline = computeDialogTimeline(dialog);
    (dialog.lines ?? []).forEach((line, i) => {
      const ex = line.expression;
      if (!ex) return;
      const sprite = (scene.sprites ?? []).find((s) => s.id === ex.spriteId);
      if (!sprite || !(sprite.states ?? []).some((st) => st.name === ex.state)) return;
      const timing = timeline.lines[i];
      if (!timing) return;
      out.push({
        lineId: line.id, spriteId: sprite.id, state: ex.state, backToDefault: !!ex.backToDefault,
        startMs: timing.startMs, endMs: timing.endMs, order: out.length,
      });
    });
  }
  return out;
}

interface Derived { kf: Keyframe; end: boolean; order: number }

const byDerivedOrder = (a: Derived, b: Derived) => a.kf.time - b.kf.time || Number(b.end) - Number(a.end) || a.order - b.order;

function groupBySprite(raw: Array<Derived & { spriteId: string }>): Map<string, Keyframe[]> {
  raw.sort(byDerivedOrder);
  const out = new Map<string, Keyframe[]>();
  for (const r of raw) {
    const list = out.get(r.spriteId) ?? [];
    list.push(r.kf);
    out.set(r.spriteId, list);
  }
  return out;
}

/**
 * The raw `state` keys the line expressions derive (spec 026), by sprite
 * id, before the precedence rule: the expression at the line's start and,
 * with `backToDefault`, the sprite's default state at its end. Untagged; ids
 * `expr-<lineId>` and `expr-<lineId>-end`; sorted by time with an end key
 * before a start key at the same time.
 */
export function lineExpressionKeyframes(scene: Pick<PlaybackScene, 'sprites' | 'dialogs'>): Map<string, Keyframe[]> {
  const raw: Array<Derived & { spriteId: string }> = [];
  for (const span of lineExpressionSpans(scene)) {
    const sprite = scene.sprites.find((s) => s.id === span.spriteId)!;
    raw.push({ spriteId: span.spriteId, end: false, order: span.order, kf: { id: `expr-${span.lineId}`, time: span.startMs, property: 'state', value: span.state } });
    if (span.backToDefault) {
      raw.push({ spriteId: span.spriteId, end: true, order: span.order, kf: { id: `expr-${span.lineId}-end`, time: span.endMs, property: 'state', value: sprite.defaultState } });
    }
  }
  return groupBySprite(raw);
}

/**
 * The line-expression keys that play in `orientation`, by sprite id, after
 * the precedence rule (rules 1 to 4 above) against each sprite's authored
 * `state` keys: dropped where an authored key lies in the line's span, an
 * end key valued by the authored keys, tagged `orientation` where the
 * counting authored keys are. Sorted as `lineExpressionKeyframes`. Sprites
 * with no kept key are absent.
 */
export function resolveLineExpressionKeys(scene: PlaybackScene, orientation: Orientation): Map<string, Keyframe[]> {
  const spans = lineExpressionSpans(scene);
  if (spans.length === 0) return new Map();
  const authored = new Map<string, Keyframe[]>();
  for (const track of scene.keyframes ?? []) authored.set(track.trackId, track.keyframes ?? []);
  const sprites = new Map<string, Sprite>(scene.sprites.map((s) => [s.id, s]));
  const counting = new Map<string, Keyframe[]>();
  const countingOf = (spriteId: string) => {
    let keys = counting.get(spriteId);
    if (!keys) {
      keys = candidateKeyframes(authored.get(spriteId) ?? [], 'state', orientation);
      counting.set(spriteId, keys);
    }
    return keys;
  };

  const raw: Array<Derived & { spriteId: string }> = [];
  for (const span of spans) {
    const keys = countingOf(span.spriteId);
    if (keys.some((k) => k.time >= span.startMs && k.time <= span.endMs)) continue;
    const tagged = (orientation === 'portrait' || orientation === 'landscape') && keys.some((k) => k.orientation === orientation);
    const tag = tagged ? { orientation } : {};
    raw.push({
      spriteId: span.spriteId, end: false, order: span.order,
      kf: { id: `expr-${span.lineId}`, time: span.startMs, property: 'state', value: span.state, ...tag },
    });
    if (span.backToDefault) {
      const back = activeKey(keys, 'state', span.endMs, orientation)?.value;
      const value = typeof back === 'string' && back ? back : sprites.get(span.spriteId)!.defaultState;
      raw.push({
        spriteId: span.spriteId, end: true, order: span.order,
        kf: { id: `expr-${span.lineId}-end`, time: span.endMs, property: 'state', value, ...tag },
      });
    }
  }
  return groupBySprite(raw);
}

/**
 * One track's playing stream (rule 5): the authored keyframes and the kept
 * derived keys sorted by time, a derived key before an authored one at the
 * same time (so the authored key wins).
 */
export function mergeDerivedKeys(authored: readonly Keyframe[], lineKeys: readonly Keyframe[]): Keyframe[] {
  if (lineKeys.length === 0) return authored as Keyframe[];
  // A stable sort keeps the derived keys (listed first, already ordered) before
  // the authored ones at equal times.
  return [...lineKeys, ...authored].sort((a, b) => a.time - b.time);
}

/**
 * The keyframes that play for `scene` in `orientation`: the authored ones
 * plus each sprite's line-expression keys, merged by the precedence rule.
 * A track without line expressions is returned as authored.
 */
export function resolvePlaybackKeyframes(scene: PlaybackScene, orientation: Orientation): KeyframeTrack[] {
  const authored = scene.keyframes ?? [];
  const derived = resolveLineExpressionKeys(scene, orientation);
  if (derived.size === 0) return authored;
  const out = authored.map((track) => {
    const keys = derived.get(track.trackId);
    return keys ? { trackId: track.trackId, keyframes: mergeDerivedKeys(track.keyframes ?? [], keys) } : track;
  });
  for (const sprite of scene.sprites) {
    const keys = derived.get(sprite.id);
    if (keys && !authored.some((t) => t.trackId === sprite.id)) out.push({ trackId: sprite.id, keyframes: keys });
  }
  return out;
}
